import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ShieldAlert, Gauge, MapPin, Users } from "lucide-react";
import { MASAR } from "@/constants/testIds";
import { BIKES } from "@/components/masar/media";
import { useLanguage } from "@/i18n/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_masar-mobile-app/artifacts/ike4zylk_MASAR%20app.png";

const APPSTORE = "https://apps.apple.com/us/app/masar-community/id6787036272";
const PLAYSTORE =
  "https://play.google.com/store/apps/details?id=com.masar.Cherif.app";

const EASE = [0.22, 1, 0.36, 1];

// Masked line-by-line reveal — plays when scrolled into view.
// whileInView lives on the outer (untranslated) mask; the translated inner
// span is a variant, because IntersectionObserver never fires on a span that
// is fully clipped by overflow-hidden at y:110%.
function Line({ children, delay = 0, className = "" }) {
  return (
    <motion.span
      className="block overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
    >
      <motion.span
        className={`block ${className}`}
        variants={{ hidden: { y: "110%" }, visible: { y: "0%" } }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  return (
    <section
      data-testid="masar-section-hero"
      className="relative border-t border-neutral-900 py-24 sm:py-32 overflow-hidden"
    >
      {/* Ambient ember glow */}
      <div className="absolute inset-x-0 top-0 h-[80vh] pointer-events-none bg-[radial-gradient(60%_50%_at_50%_0%,rgba(255,106,26,0.22),transparent_65%)]" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10">
        {/* Top meta strip */}
        <div className="flex items-center justify-between text-[10px] sm:text-xs font-mono uppercase tracking-[0.28em] text-neutral-500">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay: 0.1 }}
          >
            {t("hero.meta")}
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay: 0.2 }}
            dir="rtl"
            lang="ar"
            className="text-neutral-400 not-italic text-sm sm:text-base tracking-normal font-serif-italic"
          >
            {"مسار"}
          </motion.span>
        </div>

        {/* Signature headline */}
        <h2
          data-testid={MASAR.heroTitle}
          className="mt-14 sm:mt-20 font-serif text-white text-[15vw] sm:text-[11vw] leading-[0.92] tracking-[-0.03em] font-light"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100' }}
        >
          <Line delay={0.05}>{t("hero.h1a")}</Line>
          <Line delay={0.18}>
            <span className="font-serif-italic text-[#FF6A1A]">
              {t("hero.h1b")}
            </span>
          </Line>
          <Line delay={0.34}>{t("hero.h1c")}</Line>
          <Line delay={0.5}>
            <span className="font-serif-italic cream">{t("hero.h1d")}</span>
          </Line>
        </h2>

        {/* Meta grid under headline */}
        <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
            className="lg:col-span-5"
          >
            <p className="text-base sm:text-lg text-neutral-400 max-w-md leading-[1.55]">
              {t("hero.para")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={APPSTORE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={MASAR.heroCtaPrimary}
                className="group inline-flex items-center gap-3 rounded-full bg-[#FF6A1A] hover:bg-[#ff7d38] text-white px-6 h-12 transition-colors"
              >
                <span className="text-sm font-medium tracking-wide">
                  {t("hero.ctaPrimary")}
                </span>
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                href="#services"
                data-testid={MASAR.heroCtaSecondary}
                className="group inline-flex items-center gap-2 rounded-full border border-neutral-700 hover:border-neutral-400 text-neutral-200 px-5 h-12 transition-colors"
              >
                <span className="text-sm font-medium">{t("hero.ctaSecondary")}</span>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <MetaStat k={t("hero.statsCountries")} v="14" />
              <MetaStat k={t("hero.statsCities")} v="60+" />
              <MetaStat k={t("hero.statsSos")} v="24/7" />
            </div>

            <div className="mt-8 flex items-center gap-3">
              <StoreBadge
                platform="App Store"
                href={APPSTORE}
                testid={MASAR.heroAppStore}
              />
              <StoreBadge
                platform="Google Play"
                href={PLAYSTORE}
                testid={MASAR.heroPlayStore}
              />
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <HeroImage />
          </div>
        </div>
      </div>
    </section>
  );
}

function MetaStat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-500">
        {k}
      </div>
      <div className="mt-1 font-serif text-3xl sm:text-4xl text-white">
        {v}
      </div>
    </div>
  );
}

function StoreBadge({ platform, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-neutral-500 hover:text-white transition-colors"
    >
      <span className="h-px w-4 bg-neutral-700 group-hover:bg-[#FF6A1A] transition-colors" />
      {platform}
    </a>
  );
}

// Clipped-frame Harley photo with pointer parallax (subtle 3D moment)
function HeroImage() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const rx = useSpring(useTransform(py, [0, 1], [4, -4]), {
    stiffness: 140,
    damping: 18,
  });
  const ry = useSpring(useTransform(px, [0, 1], [-6, 6]), {
    stiffness: 140,
    damping: 18,
  });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, scale: 0.97 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.4, delay: 0.2, ease: EASE }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      className="relative"
    >
      <div className="relative photo-frame-lg spotlight-edge overflow-hidden bg-neutral-900">
        <img
          src={BIKES.heroRider}
          alt="Rider on a cruiser at sunset"
          className="w-full h-[380px] sm:h-[560px] lg:h-[640px] object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

        {/* Corner meta */}
        <div className="absolute top-6 left-6 flex items-center gap-2 rounded-full bg-black/60 backdrop-blur border border-white/10 px-3 py-1.5">
          <img
            src={LOGO_URL}
            alt="MASAR"
            className="h-5 w-5 object-contain"
          />
          <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/80">
            {t("hero.liveChip")}
          </span>
        </div>

        {/* SOS chip */}
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
          <div className="max-w-md">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-400">
              {t("hero.rideMeta")}
            </div>
            <div className="mt-2 flex items-center gap-6 font-mono text-xs text-neutral-300">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={13} /> 128 km
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Gauge size={13} /> 82 km/h
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users size={13} /> 12 riders
              </span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#FF6A1A] text-white px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.28em]">
            <ShieldAlert size={12} />
            {t("hero.sosReady")}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
